<template>
	<div class="content-block dx-card page-editor" style="padding:20px; max-width: 960px;">
		<DxLoadPanel :visible="formBusy" shading-color="rgba(0, 0, 0, 0.2)" container=".page-editor" />
		<DxForm :form-data="newPageData" label-mode="floating" @fieldDataChanged="onDataChanged" ref="pageEditorForm">
			<DxSimpleItem css-class="disable-form-paddings">
				<div style="display:flex;align-items:center;gap:10px;">
					<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
					<div style="font-style: italic; color: var(--dx-color-text-muted);">Each page is a single view for your users, with multiple components like data grids, lists, images, etc.</div>
				</div>
			</DxSimpleItem>
			<DxSimpleItem data-field="title" editor-type="dxTextBox" :label="{ text: 'Title' }" :editor-options="{ maxLength: 255 }" :isRequired="true"></DxSimpleItem>
			
			<DxGroupItem css-class="ux-settings-fieldset active always-active">
				<DxSimpleItem data-field="slug" editor-type="dxTextBox" :label="{ text: 'Page URL (Slug)' }" :editor-options="slugOptions" :isRequired="true">
					<DxRequiredRule />
					<DxPatternRule pattern="^[a-z0-9-]+$" message="Only lowercase letters, numbers and dashes are allowed" />
				</DxSimpleItem>
				<DxSimpleItem css-class="minimize-form-paddings">
					<div class="dx-info-message">
						<i class="dx-icon-info"></i><div class="dx-info-message-text">Only lowercase letters, numbers and dashes are allowed. Must be unique within the group.</div>
					</div>
				</DxSimpleItem>
			</DxGroupItem>

			<DxGroupItem :css-class="newPageData.menuShown === true ? 'ux-settings-fieldset active' : 'ux-settings-fieldset'">
				<DxSimpleItem data-field="menuShown" editor-type="dxSwitch" :label="{ template: 'iconLabel', location: 'left', text: 'eyeopen;Show in group menu' }" />
				<DxSimpleItem :visible="newPageData.menuShown === false" css-class="disable-form-paddings">
					<div style="display:flex;align-items:center;gap:10px;">
						<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
						<div style="font-style: italic; color: var(--dx-color-text-muted);">This page will not be visible in the menu, unless you are an group admin. Someone with the URL may still access it.</div>
					</div>
				</DxSimpleItem>
				<DxSimpleItem :visible="newPageData.menuShown === true" css-class="disable-form-paddings">
					<div style="display:flex;align-items:center;gap:10px;">
						<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
						<div style="font-style: italic; color: var(--dx-color-text-muted);">Pages are normally shown in the menu on the left for all users who have access to them.</div>
					</div>
				</DxSimpleItem>
				<DxGroupItem :visible="newPageData.menuShown === true">
					<DxSimpleItem data-field="menuLabel" :label="{ text: 'Title in menu' }" :editor-options="{ maxLength: 255 }" :isRequired="true" />
					<DxSimpleItem data-field="menuIcon" :label="{ text: 'Icon shown in menu' }" editor-type="dxLookup" :editor-options="iconLookupEditor" />
				</DxGroupItem>
			</DxGroupItem>

			<DxGroupItem :css-class="newPageData.useContext === true ? 'ux-settings-fieldset active' : 'ux-settings-fieldset'">
				<DxSimpleItem data-field="useContext" editor-type="dxSwitch" :label="{ template: 'iconLabel', location: 'left', text: 'parentfolder;Allow context to be passed to this page' }" />
				<DxSimpleItem :visible="newPageData.useContext !== true" css-class="disable-form-paddings">
					<div style="display:flex;align-items:center;gap:10px;">
						<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
						<div style="font-style: italic; color: var(--dx-color-text-muted);">This page is always shown the same way to everyone.</div>
					</div>
				</DxSimpleItem>
				<DxSimpleItem :visible="newPageData.useContext === true" css-class="disable-form-paddings">
					<div style="display:flex;align-items:center;gap:10px;">
						<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
						<div style="font-style: italic; color: var(--dx-color-text-muted);">This page must be passed a certain object to know what to show. Select one or more types to allow.</div>
					</div>
				</DxSimpleItem>
				<DxGroupItem :visible="newPageData.useContext === true">
					<DxSimpleItem data-field="contextSchemas" editor-type="dxTagBox" :editor-options="contextSchemaOptions" :label="{ text: 'Blueprints allowed' }" />
				</DxGroupItem>
			</DxGroupItem>

			<DxGroupItem :css-class="newPageData.accessCheck === true ? 'ux-settings-fieldset active' : 'ux-settings-fieldset'">
				<DxSimpleItem data-field="accessCheck" editor-type="dxSwitch" :label="{ template: 'iconLabel', location: 'left', text: 'lock;Restrict access to this page' }" />
				<DxSimpleItem :visible="newPageData.accessCheck === false" css-class="disable-form-paddings">
					<div style="display:flex;align-items:center;gap:10px;">
						<img src="../assets/placeholder.jpg" style="width:144px;height:48px;flex-shrink:0" class="placeholder">
						<div style="font-style: italic; color: var(--dx-color-text-muted);">This page is accessible to all members of the group, but you may restrict access further.</div>
					</div>
				</DxSimpleItem>
				<DxGroupItem :visible="newPageData.accessCheck === true">
					<DxSimpleItem css-class="disable-form-paddings">
						<permissions-editor v-model="newPageData.accessMembers" type="page" />
					</DxSimpleItem>
				</DxGroupItem>
			</DxGroupItem>

			<DxSimpleItem>
				<div style="display:flex;flex-direction:row-reverse;justify-content:space-between;">
					<DxButton type="success" :icon="isEditing ? 'save' : 'add'" :text="isEditing ? 'Update settings immediately' : 'Create page'" @click="submitPage" />
					<DxButton v-if="showCancel" type="info" styling-mode="text" icon="close" text="Cancel" @click="$emit('cancel')" />
				</div>
			</DxSimpleItem>

			<template #iconLabel="{ data: label }">
				<i :class="'checkbox-label-icon dx-icon-' + label.text.split(';')[0]"></i>
				<span>
					{{ label.text.split(';')[1] }}
				</span>
			</template>
			<template #iconItem="{ data }">
				<i v-if="data" :class="'dx-icon-' + data"></i>
				<span v-if="data">{{ data }}</span>
			</template>
			<template #iconField="{ data }">
				<i v-if="data" :class="'dx-icon-' + data" style="font-size: 18px;"></i>
				<span v-if="data" style="margin-left: 10px;">{{ data }}</span>
			</template>
		</DxForm>

		<DxPopover v-model:visible="slugHintPopover" target="#slug-hint-button" position="top" width="400">
			<div class="dx-info-message">
				<i class="dx-icon-info"></i>
				<div class="dx-info-message-text">This part of the URL is configured in the group settings by the administrator.</div>
			</div>
		</DxPopover>
	</div>
</template>

<style lang="scss">
#slug-hint-button .dx-button-text{
	text-transform: lowercase;
}
.popup-iconselect.dx-lookup-popup-wrapper .dx-list-items {
	display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-content: flex-start;
    justify-content: space-between;
    align-items: flex-start;
	> .dx-list-item {
		width: 200px;
		i {
			font-size: 24px;
		}
		span {
			line-height: 24px;
			vertical-align: top;
			margin-left: 5px;
		}
	}
	> .dx-list-item:first-of-type {
		margin-top: 0;
	}
}
</style>

<script setup>
/* eslint-disable */
import { ref, nextTick, watch, computed, defineProps, inject } from 'vue';
import { DxForm, DxSimpleItem, DxGroupItem } from 'devextreme-vue/form';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import DxButton from 'devextreme-vue/button';
import { DxPopover } from 'devextreme-vue/popover';
import { DxRequiredRule, DxPatternRule } from 'devextreme-vue/validator';
import "devextreme/ui/lookup";
import notify from 'devextreme/ui/notify';
import { alert as dxAlert } from 'devextreme/ui/dialog';
import { useRouter } from 'vue-router';
import appInfo from '@/app-info';
import slugify from 'slugify';
import icons from "../data/icons";
import { SchemaCache } from '../schemas.js';
import PermissionsEditor from './permissions-editor.vue';

const props = defineProps([
	'page',			// The page object we are editing, or an empty object if creating a new page. Values are not changed in this object.
	'group',		// The group object holding the group for this page. Should contain at least a slug and id. The group_id will be copied to the submitted page.
	'showCancel',	// If true, show a cancel button in the form.
]);
const emit = defineEmits([
	'cancel',		// Cancel button clicked by user.
	'updated',		// Page updated by user (only used upon update).
	'created',		// Page created by user (only used upon creation).
]);
const $http = inject('$http');
const router = useRouter();

const pageEditorForm = ref(null);			// Reference to DxForm component.
const formBusy = ref(false);				// If form is busy fetching data or submitting data and should be blocked
const slugHintPopover = ref(false);			// If the hint for slug popover should be shown
const groupUrlPrefix = ref('');				// The URL prefix UI hint including base URL and group slug
const newPageData = ref({
	title: '',
	slug: '',
	menuShown: true,
	menuLabel: '',
	menuIcon: '',
	useContext: false,
	contextSchemas: [],
	accessCheck: false,
	accessMembers: [],
});

// Watch for prop changes and update our local data being edited, as well as group data slug calculation.
watch(props,
	async (newProps) => {
		groupUrlPrefix.value = appInfo.slugBaseURL + '/' + (newProps.group?.slug || '') + '/';
		// We edit only the primary attributes (title, slug) as well as everything in the page settings.
		let settingsClone = newProps.page && newProps.page.settings ? JSON.parse(JSON.stringify(newProps.page.settings)) : {};
		for (let k in newPageData.value) {
			if ((k == 'title' || k == 'slug') && (k in newProps.page)) {
				newPageData.value[k] = String(newProps.page[k]);
			} else if (settingsClone && k in settingsClone) {
				newPageData.value[k] = settingsClone[k];
			}
		}
	},
	{ immediate: true }
);

// Any changes are edits if the underlying page has an id that is not 0.
const isEditing = computed(() => props.page && props.page.id);

// When the main page title is changed, update menu title and slug if it has not been changed yet.
// But make sure we don't trigger the label change event if we are automatically changing it.
const disableAutoChanges = { menuLabel: false, slug: false };
let insideAutoChange = false;
function onDataChanged({ dataField, value }) {
	if (insideAutoChange)
		return;
	if (dataField === 'title') {
		updateDependentFields(value);
	} else if (dataField in disableAutoChanges) {
		disableAutoChanges[dataField] = true;
	}
}
function updateDependentFields(title) {
	const shouldUpdateMenu = !disableAutoChanges.menuLabel || !newPageData.value.menuLabel;
	const shouldUpdateSlug = !disableAutoChanges.slug || !newPageData.value.slug;

	if (shouldUpdateMenu || shouldUpdateSlug) {
		insideAutoChange = true;
		if (shouldUpdateMenu) {
			disableAutoChanges.menuLabel = false;
			newPageData.value.menuLabel = title;
		}
		if (shouldUpdateSlug) {
			disableAutoChanges.slug = false;
			newPageData.value.slug = slugify(title, { replacement: '-', lower: true, strict: true, trim: true });
			if (newPageData.value.slug.length > 120) {
				newPageData.value.slug = newPageData.value.slug.substring(0, 120);
			}
		}
		nextTick(() => insideAutoChange = false);
	}
}

// Handle for submitting new/updated page to server. We must store all our settings in JSON object except for a few key fields.
async function submitPage() {
	const formValid = pageEditorForm.value.instance.validate();
	if (!formValid || !formValid.isValid) {
		return;
	}
	formBusy.value = true;
	const settingsToSave = { ...newPageData.value };
	delete settingsToSave.title; delete settingsToSave.slug;
	let submittedParams = {
		title: newPageData.value.title,
		slug: newPageData.value.slug,
		settings: settingsToSave,
		group_id: props.group.id,
	};
	// If editing, add the page ID, but if created, we expect an empty layout/components. In the future, this can be replaced with whatever pre-made templating system we add.
	if (isEditing.value) {
		submittedParams.id = props.page.id;
	} else {
		submittedParams.layout = {};
		submittedParams.components = {};
	}
	const submitMethod = isEditing.value ? 'patch' : 'post';
	const submitPath = isEditing.value ? `/page/${props.page.id}` : '/page';
	$http[submitMethod](submitPath, submittedParams).then(response => {
		console.log("Page " + (isEditing.value ? 'updated' : 'created') + ':', response);
		formBusy.value = false;
		notify({ message: (isEditing.value ? "Page settings updated" : "Page created successfully"), type: "success", displayTime: 2000, position: "top right" });
		emit(isEditing.value ? 'updated' : 'created', response.data);
	}).catch(error => {
		console.error(error);
		formBusy.value = false;
		dxAlert(`<div class="dx-error-message"><i class="dx-icon-warning"></i><div class="dx-error-message-text">${error.response?.data?.message || 'Failed to ' + (isEditing.value ? 'update' : 'create') + ' page'}</div></div>`, `Error ${error.response?.status || '0'} / ${error.response?.statusText || 'Unknown'}`);
	});
}

// Slug editor options - we show the slug prefix (group slug) and ending to denote this is a URL better.
const slugOptions = computed(() => ({
	labelMode: 'outside',
	placeholder: 'my-page-url',
	maxLength: 120,
	buttons: [
		{  name: 'prefix', location: 'before', options: { 
			stylingMode: 'text', 
			text: groupUrlPrefix.value, 
			elementAttr: { id: 'slug-hint-button' },
			onClick: () => {
				slugHintPopover.value = !slugHintPopover.value;
			} 
		}},
	],
}));

// Icon lookup editor options.
// TODO: Refactor into a component.
const iconLookupEditor = {
	dataSource: icons,
	itemTemplate: 'iconItem',
	fieldTemplate: 'iconField',
	searchEnabled: true,
	showClearButton: true,
	usePopover: true,
	dropDownOptions: {
		wrapperAttr: {
			class: 'popup-iconselect',
		},
		deferRendering: false,			// Setting this to true causes lookup not to work or show icons at all!
	},
};

// Context/argument schema editor options, allowing selection of multiple possible schemas to be allowed in the context for this page.
const contextSchemaOptions = {
	dataSource: SchemaCache.getAllSchemasExceptRelations(),
	valueExpr: 'type',
	displayExpr: 'singular',
};

</script>